<template>
  <!-- Properties bar — fine fields for the selected element. Type/size/mirror/
       zone-colour are edited in the sub-bar pickers above. Always rendered at a
       fixed height so selecting an element never shifts the board layout.

       Inputs keep the native :value + @change contract (the selected element is
       the source of truth and the parent coerces the raw string value), so they
       use the shared .st-control class rather than the v-model primitives. -->
  <div class="props-bar">
    <span v-if="!kind || !element" class="props-empty"
      >Select an element to edit its properties</span
    >

    <span v-if="kind && element" class="props-title st-eyebrow">{{ kind }}</span>

    <template
      v-if="
        element &&
        (kind === 'Objective' || kind === 'Terrain' || kind === 'Label' || kind === 'Icon')
      "
    >
      <label class="props-field"
        ><span>X</span>
        <input
          class="st-control"
          type="number"
          step="0.5"
          min="0"
          max="60"
          :value="element.x"
          @change="emitField('x', $event)"
      /></label>
      <label class="props-field"
        ><span>Y</span>
        <input
          class="st-control"
          type="number"
          step="0.5"
          min="0"
          max="44"
          :value="element.y"
          @change="emitField('y', $event)"
      /></label>
    </template>

    <template v-if="element && kind === 'Label'">
      <label class="props-field props-text"
        ><span>Text</span>
        <input
          class="st-control"
          type="text"
          :value="element.text"
          @change="emitField('text', $event)"
      /></label>
      <label class="props-field"
        ><span>Font</span>
        <input
          class="st-control"
          type="number"
          step="1"
          min="6"
          max="96"
          placeholder="auto"
          :value="element.fontSize ?? ''"
          @change="emitField('fontSize', $event)"
      /></label>
      <StButton
        size="sm"
        variant="ghost"
        icon="rotate-right"
        title="Rotate 90°"
        @click="$emit('field', 'rotation', ((element.rotation ?? 0) + 90) % 360)"
        >{{ element.rotation ?? 0 }}°</StButton
      >
    </template>

    <template v-if="element && kind === 'Icon'">
      <label class="props-field props-text"
        ><span>Legend</span>
        <input
          class="st-control"
          type="text"
          placeholder="(optional)"
          :value="element.legend ?? ''"
          @change="emitField('legend', $event)"
      /></label>
      <label class="props-field"
        ><span>Size</span>
        <input
          class="st-control"
          type="number"
          step="1"
          min="4"
          max="96"
          placeholder="auto"
          :value="element.size ?? ''"
          @change="emitField('size', $event)"
      /></label>
    </template>

    <template v-if="element && (kind === 'Zone' || kind === 'Arrow')">
      <label class="props-field"
        ><span>X1</span>
        <input
          class="st-control"
          type="number"
          step="0.5"
          min="0"
          max="60"
          :value="element.x1"
          @change="emitField('x1', $event)"
      /></label>
      <label class="props-field"
        ><span>Y1</span>
        <input
          class="st-control"
          type="number"
          step="0.5"
          min="0"
          max="44"
          :value="element.y1"
          @change="emitField('y1', $event)"
      /></label>
      <label class="props-field"
        ><span>X2</span>
        <input
          class="st-control"
          type="number"
          step="0.5"
          min="0"
          max="60"
          :value="element.x2"
          @change="emitField('x2', $event)"
      /></label>
      <label class="props-field"
        ><span>Y2</span>
        <input
          class="st-control"
          type="number"
          step="0.5"
          min="0"
          max="44"
          :value="element.y2"
          @change="emitField('y2', $event)"
      /></label>
    </template>

    <template v-if="element && kind === 'Arrow'">
      <label class="props-field props-text"
        ><span>Text</span>
        <input
          class="st-control"
          type="text"
          placeholder="(optional)"
          :value="element.text ?? ''"
          @change="emitField('text', $event)"
      /></label>
    </template>

    <template v-if="element && kind === 'Zone'">
      <label class="props-field props-text"
        ><span>Label</span>
        <textarea
          class="st-control zone-label-input"
          rows="2"
          placeholder="(default)"
          :value="element.label ?? ''"
          @input="emitField('label', $event)"
        />
      </label>
      <label class="props-field"
        ><span>Label dX</span>
        <input
          class="st-control"
          type="number"
          step="0.5"
          placeholder="0"
          :value="element.labelDx ?? ''"
          @change="emitField('labelDx', $event)"
      /></label>
      <label class="props-field"
        ><span>Label dY</span>
        <input
          class="st-control"
          type="number"
          step="0.5"
          placeholder="0"
          :value="element.labelDy ?? ''"
          @change="emitField('labelDy', $event)"
      /></label>
      <StCheckbox
        :model-value="element.hideLabel ?? false"
        @update:model-value="$emit('field', 'hideLabel', $event)"
      >
        Hide label
      </StCheckbox>
    </template>

    <StButton
      v-if="kind && element"
      class="props-delete"
      variant="danger"
      size="sm"
      icon="trash"
      @click="$emit('delete')"
      >Delete</StButton
    >
  </div>
</template>

<script setup lang="ts">
import { StButton, StCheckbox } from '../ui/index.js';

defineProps<{ kind: string | null; element: any }>();
const emit = defineEmits<{ field: [name: string, value: unknown]; delete: [] }>();

function emitField(name: string, e: Event) {
  emit('field', name, (e.target as HTMLInputElement).value);
}
</script>

<style scoped>
.props-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--st-space-2) var(--st-space-4);
  padding: var(--st-space-2) var(--st-space-3);
  /* Always present at a constant height (box-sizing: border-box) so selecting an
     element — e.g. when a drag begins — never inserts a row that shifts the board.
     Sized to fit the tallest control (the 2-line zone label textarea below). */
  min-height: 3.625rem;
  background: var(--st-surface-sunken);
  border-top: 1px solid var(--st-border);
  font-size: var(--st-text-sm);
}

.props-empty {
  color: var(--st-text-subtle);
  font-style: italic;
}

.props-title {
  color: var(--st-text-muted);
}

.props-field {
  display: inline-flex;
  align-items: center;
  gap: var(--st-space-1);
  color: var(--st-text-muted);
  white-space: nowrap;
}

/* Compact, inline override of the shared control sizing for this dense bar. */
.props-field .st-control {
  width: 4rem;
  min-height: var(--st-control-h-sm);
  padding: var(--st-space-1) var(--st-space-2);
  font-size: var(--st-text-sm);
}
.props-field.props-text .st-control {
  width: 9rem;
}
/* Multi-line zone label: fixed 2-line height the bar reserves space for; users
   can still drag it taller. */
.props-field .zone-label-input {
  resize: vertical;
  align-self: center;
  height: 40px;
  line-height: 1.2;
  font-family: inherit;
}

.props-delete {
  margin-left: auto;
}
</style>
